/**
 * Engine Debug Log — lightweight event bus for engine calculation runs.
 *
 * Controlled by `?debug=true` URL param or `localStorage.engine_debug = "1"`.
 * The floating EngineDebugPanel reads from this store.
 */

export interface EngineDebugEntry {
  slug: string;
  timestamp: number;
  input: Record<string, unknown>;
  output: {
    matched: boolean;
    configLabel: string | null;
    materialCount: number;
    subtotal: number;
  };
}

const MAX_ENTRIES = 10;
let entries: EngineDebugEntry[] = [];
// Snapshot reference — must change identity on every mutation so
// useSyncExternalStore triggers a re-render.
let snapshot: readonly EngineDebugEntry[] = entries;
const listeners = new Set<() => void>();

function notify() {
  snapshot = [...entries]; // new reference for React
  listeners.forEach((fn) => fn());
}

/* ── Public API ─────────────────────────────────────────── */

export function isEngineDebugEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') return true;
    return localStorage.getItem('engine_debug') === '1';
  } catch {
    return false;
  }
}

export function pushDebugEntry(entry: EngineDebugEntry): void {
  entries = [entry, ...entries].slice(0, MAX_ENTRIES);
  notify();
}

export function getDebugEntries(): readonly EngineDebugEntry[] {
  return snapshot;
}

export function clearDebugEntries(): void {
  entries = [];
  notify();
}

export function subscribeDebug(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
